import React, { useState, useMemo } from "react";
import WithModal from "../../global/modal/WithModal/WithModal";
import CourseTable from "../../table/CourseTable";
import testIcon from "../../../assets/icons/test.svg";
import TablePagination from "../../table/TablePagination";
import ContentRepositoryComp from "../../createContentRepo/ContentRepositoryComp";
import { ReactComponent as Cancel } from "../../../assets/icons/cancel.svg";
import propTypes from "prop-types";
import BreadCrumb from "../../global/breadcrumbs/BreadCrumb";
import { apirequest } from "../../../API/api";
import { endpoint } from "../../../API/config";
import { useQuery } from "react-query";
import { addAssets } from "../../../redux/features/learningPath/learningPathSlice";
import { tempSetAssets } from "../../../redux/features/learningPath/learningPathSlice";
import {} from "../../../redux/features/learningPath/learningPathSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import useDebounce from "../../../hooks/useDebounce";
import { SpinnerCircular } from "spinners-react";

const MapAssetModal = ({
  closeModal,
  setDisableSave,
  learningPathId,
  addingToTier = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedData, setSelectedData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [repoId, setRepoId] = useState("");
  const [showAsset, setShowAsset] = useState(false);
  
  const dataPerPage = 4;
  const dispatch = useDispatch();
  
  // Handle search with pagination reset
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    setCurrentPage(1); // Reset to first page on search
  };
  
  const debouncedSearch = useDebounce(searchValue, 800);
  const variantId = useSelector(state => state.variantsByCourseData.variantsByCourse.variantId);
  const lpID = learningPathId || useSelector(state => state.learningPathData.selectedLearningPathId);
  const login_Details = useSelector(state => state?.loginData?.LoginDetails);
  
  const column = [
    { col: "0", width: "35%" },
    { col: "1", width: "55%" },
    { col: "2", width: "" },
  ];

  // Get Repository API
  const { data: getContentRepoData, status: contentStatus, isLoading } = useQuery(
    [`${endpoint.content_repo.key}`, debouncedSearch],
    () =>
      apirequest({
        url: `${endpoint.content_repo.addr}`,
        method: "POST",
        data: {
          adminimspin: login_Details.userImsPin,
          searchKeyword: debouncedSearch,
        },
      }),
  );


  const repositoryData = useMemo(() => {
    return getContentRepoData?.data?.data?.ContentRepositoryList || [];
  }, [getContentRepoData]);

  // Pagination calculations
  const indexOfLastItem = currentPage * dataPerPage;
  const indexOfFirstItem = indexOfLastItem - dataPerPage;
  const currentData = repositoryData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(repositoryData.length / dataPerPage);

  const paginate = (pageNumbers) => {
    setCurrentPage(pageNumbers);
  };


  const mapAsset = async () => {
    return apirequest({
      url: `${endpoint.add_LearningPath_asset.addr}`,
      method: "POST",
      data: {
        variantId,
        learningPathId: lpID,
        assetData: selectedData,
        createdBy: login_Details.userImsPin,
      },
    });
  };

  const { refetch: mapAssetToLP } = useQuery(
    endpoint.add_LearningPath_asset.key,
    mapAsset,
    {
      enabled: false,
      onSuccess: res => {
        const addAsetObj = {
          selectedData: res.data.data.assetData,
          addingToTier,
          learningPathId: lpID,
        };
        dispatch(tempSetAssets(addAsetObj));
        dispatch(addAssets(addAsetObj));
        setDisableSave(false);
        closeModal();
      },
      onError: err => {
        alert(err.data.message);
        closeModal();
      }
    }
  );

  const handleSelectRepo = (id) => {
    setRepoId(id);
    setShowAsset(true);
  };

  const handleSaveAssets = () => {
    mapAssetToLP();
  };

  const renderRepositoryContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-[400px]">
          <SpinnerCircular 
            size={50} 
            thickness={100} 
            speed={100} 
            color="#00ABFB" 
            secondaryColor="#f3f4f6" 
          />
        </div>
      );
    }

    if (contentStatus === "success") {
      if (repositoryData.length === 0) {
        return (
          <div className="flex items-center justify-center h-[300px] text-gray-500">
            {searchValue ? 'No repositories found matching your search.' : 'No repositories available.'}
          </div>
        );
      }

      return (
        <>
          <div className="rounded-2xl shadow-outer p-5">
            <CourseTable
              currentData={currentData}
              column={column}
              colIcon={testIcon}
              dataForContentRepo={true}
              isforAddConentRepo={true}
              setContentRepoId={handleSelectRepo}
            />
          </div>
          
          {repositoryData.length > dataPerPage && (
            <TablePagination
              data={repositoryData}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              paginate={paginate}
              dataPerPage={dataPerPage}
              totalPages={totalPages}
            />
          )}
        </>
      );
    }

    return null;
  };

  return (
    <div>
      <div className="p-5 w-[64rem]">
        <div className="flex justify-between mb-8 pl-[27rem]">
          <h1 className="font-ibm text-lg font-medium">Map Asset</h1>
          <Cancel className="cursor-pointer mr-4" onClick={closeModal} />
        </div>
        
        {!showAsset ? (
          <div className="relative">
            <BreadCrumb />
            
            {/* Search Input */}
            <div className="mb-4 flex justify-end">
              <input 
                type="text" 
                value={searchValue}
                onChange={handleSearch}  
                placeholder="Search by repository name" 
                className="w-96 border-2 border-secondary-300 rounded-2xl pl-4 pr-5 py-3 border-solid px-2 focus:outline-none placeholder-bold font-semibold text-xl focus:placeholder-transparent"
              />
            </div>
            
            {/* Content Area with consistent height */}
            <div className="min-h-[400px]">
              {renderRepositoryContent()}
            </div>
          </div>
        ) : (
          <>
            <p
              className="underline ml-6 cursor-pointer"
              onClick={() => {
                setShowAsset(false);
                setRepoId("");
              }}>
              Back
            </p>
            <ContentRepositoryComp
              isforAddConentRepo={true}
              selectedData={selectedData}
              setSelectedData={setSelectedData}
              contentRepoID={repoId}
            />
          </>
        )}
        
        <div className="flex justify-center mt-5 space-x-5">
          <button
            className="px-10 py-3 bg-primary-300 text-white rounded-2xl shadow-outer"
            onClick={handleSaveAssets}>
            Save
          </button>
          <button
            className="px-10 py-3 font-medium text-base rounded-2xl shadow-outer"
            onClick={() => closeModal()}>
            Exit
          </button>
        </div>
      </div>
    </div>
  );
};

MapAssetModal.propTypes = {
  closeModal: propTypes.func,
  setLearningPath: propTypes.object,
  setDisableSave: propTypes.func,
  learningPathId: propTypes.string,
  addingToTier: propTypes.bool,
};

export default WithModal(MapAssetModal);